/**
 * Deterministic maze generator (recursive backtracker) used for the brand
 * artwork. Same seed → same maze, so the hero never "jumps" between renders.
 */

export type Cell = { x: number; y: number; walls: [boolean, boolean, boolean, boolean] } // top, right, bottom, left

export type Maze = {
  cols: number
  rows: number
  cells: Cell[]
  /** cell indices from entrance (0,0) to exit (cols-1, rows-1) */
  solution: number[]
}

export function mulberry32(seed: number) {
  let a = seed >>> 0
  return () => {
    a = (a + 0x6d2b79f5) >>> 0
    let t = a
    t = Math.imul(t ^ (t >>> 15), t | 1)
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61)
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296
  }
}

const DIRS = [
  [0, -1],
  [1, 0],
  [0, 1],
  [-1, 0],
] as const

export function generateMaze(cols: number, rows: number, seed = 1): Maze {
  const rnd = mulberry32(seed)
  const cells: Cell[] = []
  for (let y = 0; y < rows; y++) {
    for (let x = 0; x < cols; x++) cells.push({ x, y, walls: [true, true, true, true] })
  }
  const idx = (x: number, y: number) => y * cols + x
  const visited = new Uint8Array(cells.length)
  const stack: number[] = [0]
  visited[0] = 1

  while (stack.length) {
    const cur = stack[stack.length - 1]
    const c = cells[cur]
    const options: number[] = []
    for (let d = 0; d < 4; d++) {
      const nx = c.x + DIRS[d][0]
      const ny = c.y + DIRS[d][1]
      if (nx < 0 || ny < 0 || nx >= cols || ny >= rows) continue
      if (!visited[idx(nx, ny)]) options.push(d)
    }
    if (!options.length) {
      stack.pop()
      continue
    }
    const d = options[Math.floor(rnd() * options.length)]
    const n = idx(c.x + DIRS[d][0], c.y + DIRS[d][1])
    c.walls[d] = false
    cells[n].walls[(d + 2) % 4] = false
    visited[n] = 1
    stack.push(n)
  }

  // BFS for the solution path
  const prev = new Int32Array(cells.length).fill(-1)
  const seen = new Uint8Array(cells.length)
  const queue: number[] = [0]
  seen[0] = 1
  const target = cells.length - 1
  while (queue.length) {
    const cur = queue.shift()!
    if (cur === target) break
    const c = cells[cur]
    for (let d = 0; d < 4; d++) {
      if (c.walls[d]) continue
      const n = idx(c.x + DIRS[d][0], c.y + DIRS[d][1])
      if (seen[n]) continue
      seen[n] = 1
      prev[n] = cur
      queue.push(n)
    }
  }
  const solution: number[] = []
  for (let at = target; at !== -1; at = prev[at]) solution.push(at)
  solution.reverse()

  return { cols, rows, cells, solution }
}

/** SVG path data for all walls, in cell units (multiply by `size`). */
export function wallsPath(maze: Maze, size: number): string {
  const parts: string[] = []
  // outer top + left edges once, then each cell's right/bottom
  parts.push(`M0 0H${maze.cols * size}`)
  parts.push(`M0 0V${maze.rows * size}`)
  for (const c of maze.cells) {
    const x = c.x * size
    const y = c.y * size
    if (c.walls[1]) parts.push(`M${x + size} ${y}v${size}`)
    if (c.walls[2]) parts.push(`M${x} ${y + size}h${size}`)
  }
  return parts.join('')
}

/** SVG path data for the solution, through cell centres. */
export function solutionPath(maze: Maze, size: number): string {
  const h = size / 2
  return maze.solution
    .map((i, n) => {
      const c = maze.cells[i]
      return `${n === 0 ? 'M' : 'L'}${c.x * size + h} ${c.y * size + h}`
    })
    .join('')
}
